import { useCallback, useEffect, useRef, useState } from "react";

const NEAR_BOTTOM_THRESHOLD_PX = 120;
const FLASH_HIGHLIGHT_DURATION_MS = 1200;

export const useConversationAutoScroll = ({
  newestMessageCount,
  progressEventCount,
  newestMessageId,
  newestAgentMessageId,
}: {
  newestMessageCount: number;
  progressEventCount: number;
  newestMessageId: string | null;
  newestAgentMessageId: string | null;
}) => {
  const scrollAnchorRef = useRef<HTMLDivElement | null>(null);
  const mainElementRef = useRef<HTMLElement | null>(null);
  const isReaderNearBottomRef = useRef(true);
  const previouslySeenAgentIdRef = useRef<string | null>(null);
  const hasObservedFirstNonEmptyThreadRef = useRef(false);
  const [hasNewMessage, setHasNewMessage] = useState(false);

  const registerMainElement = useCallback((node: HTMLElement) => {
    mainElementRef.current = node;
  }, []);

  useEffect(() => {
    const scroller = mainElementRef.current?.parentElement ?? null;
    if (!scroller) return;

    const updateNearBottom = () => {
      const distanceFromBottom =
        scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
      const isNearBottom = distanceFromBottom <= NEAR_BOTTOM_THRESHOLD_PX;
      isReaderNearBottomRef.current = isNearBottom;
      if (isNearBottom) {
        setHasNewMessage(false);
      }
    };

    scroller.addEventListener("scroll", updateNearBottom, { passive: true });
    return () => scroller.removeEventListener("scroll", updateNearBottom);
  }, []);

  const isThreadEmpty = newestMessageCount === 0 && progressEventCount === 0;

  useEffect(() => {
    if (isThreadEmpty) {
      previouslySeenAgentIdRef.current = newestAgentMessageId;
      return;
    }

    const wasInitialized = hasObservedFirstNonEmptyThreadRef.current;
    hasObservedFirstNonEmptyThreadRef.current = true;

    const isNewAgentMessageArrival =
      wasInitialized &&
      newestAgentMessageId !== null &&
      newestAgentMessageId !== previouslySeenAgentIdRef.current;

    previouslySeenAgentIdRef.current = newestAgentMessageId;

    if (isReaderNearBottomRef.current) {
      scrollAnchorRef.current?.scrollIntoView({ block: "end" });
    } else if (isNewAgentMessageArrival) {
      setHasNewMessage(true);
    }
  }, [
    newestMessageCount,
    progressEventCount,
    newestMessageId,
    isThreadEmpty,
    newestAgentMessageId,
  ]);

  const jumpToLatest = useCallback(() => {
    scrollAnchorRef.current?.scrollIntoView({
      block: "end",
      behavior: "smooth",
    });
    setHasNewMessage(false);
  }, []);

  return {
    registerMainElement,
    scrollAnchorRef,
    hasNewMessage,
    jumpToLatest,
  };
};

export const useFlashHighlightOnNewMessage = (
  newestMessageId: string | null
) => {
  const [highlightedMessageId, setHighlightedMessageId] = useState<
    string | null
  >(null);
  const previouslySeenNewestIdRef = useRef<string | null>(null);

  useEffect(() => {
    const isNewArrivalAfterFirstRender =
      newestMessageId !== null &&
      previouslySeenNewestIdRef.current !== null &&
      newestMessageId !== previouslySeenNewestIdRef.current;

    previouslySeenNewestIdRef.current = newestMessageId;

    if (!isNewArrivalAfterFirstRender) {
      return undefined;
    }

    setHighlightedMessageId(newestMessageId);
    const clearHighlightTimer = setTimeout(
      () => setHighlightedMessageId(null),
      FLASH_HIGHLIGHT_DURATION_MS
    );
    return () => clearTimeout(clearHighlightTimer);
  }, [newestMessageId]);

  return highlightedMessageId;
};
